import React from 'react';
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, HelpCircle } from "lucide-react";

interface AssessmentQuestionProps {
  title: string;
  question: string;
  options: {
    text: string;
    points: number;
  }[];
  evidence: {
    title: string;
    description: string;
  }[];
  onNext: () => void;
  onPrevious: () => void;
  onSelect: (points: number) => void;
  selectedPoints?: number;
  showPrevious?: boolean;
}

export const AssessmentQuestion: React.FC<AssessmentQuestionProps> = ({
  title,
  question,
  options,
  evidence,
  onNext,
  onPrevious,
  onSelect,
  selectedPoints,
  showPrevious = true,
}) => {
  return (
    <Card className="p-8 space-y-8 bg-gradient-card border-0 shadow-lg max-w-4xl mx-auto">
      {/* Question Header */}
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-2xl font-semibold text-text-primary mb-2">{title}</h3>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">{question}</p>
        </div>
        
        {/* Instructions */}
        <div className="bg-accent-blue/10 p-4 rounded-lg border-l-4 border-accent-blue">
          <div className="flex items-start gap-2">
            <HelpCircle className="w-5 h-5 text-accent-blue mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-text-primary font-medium mb-1">How to respond:</p>
              <p className="text-sm text-text-secondary">
                Click on the option that best describes your experience. Choose based on what you can 
                demonstrate with specific examples, not aspirations. When in doubt, pick the more conservative option.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-text-primary mb-4">Select the option that best describes you:</h4>
        
        <RadioGroup
          value={selectedPoints?.toString()}
          onValueChange={(value) => onSelect(parseInt(value))}
          className="space-y-3"
        >
          {options.map((option, index) => (
            <div 
              key={index} 
              className="group relative cursor-pointer"
              onClick={() => onSelect(option.points)}
            >
              <div className={`
                flex items-start space-x-4 p-6 rounded-lg border-2 transition-all duration-200
                ${selectedPoints === option.points 
                  ? 'border-primary bg-primary/10 shadow-md' 
                  : 'border-border hover:border-primary/50 hover:bg-primary/5'
                }
              `}>
                <RadioGroupItem
                  value={option.points.toString()}
                  id={`option-${index}`}
                  className="mt-1 flex-shrink-0"
                />
                
                <div className="flex-1 min-w-0">
                  <Label 
                    htmlFor={`option-${index}`}
                    className="text-base font-medium text-text-primary cursor-pointer leading-relaxed"
                  >
                    {option.text}
                  </Label>
                  
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs font-semibold px-2 py-1 bg-primary/20 text-primary rounded">
                      {option.points} points
                    </span>
                    {selectedPoints === option.points && (
                      <span className="text-xs text-accent-green font-medium">✓ Selected</span>
                    )}
                  </div>
                </div>
                
                <ChevronRight className={`
                  w-5 h-5 transition-all duration-200 flex-shrink-0 mt-1
                  ${selectedPoints === option.points ? 'text-primary' : 'text-text-muted group-hover:text-primary'}
                `} />
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Evidence section */}
      {evidence.length > 0 && (
        <div className="space-y-4 pt-6 border-t border-border/50">
          <h4 className="text-lg font-medium text-text-primary">Evidence to Consider:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {evidence.map((item, index) => (
              <div key={index} className="bg-background/50 p-4 rounded-lg border border-border/30">
                <h5 className="font-medium text-text-primary mb-2">{item.title}</h5>
                <p className="text-sm text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-border/50">
        {showPrevious ? (
          <Button 
            variant="outline" 
            onClick={onPrevious}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
        ) : (
          <div></div>
        )}
        
        <div className="text-center">
          {selectedPoints ? (
            <p className="text-sm text-text-muted">
              Answer selected • Click Next to continue
            </p>
          ) : (
            <p className="text-sm text-text-muted">
              Please select an answer to continue
            </p>
          )}
        </div>
        
        <Button 
          onClick={onNext}
          disabled={!selectedPoints}
          className={`
            flex items-center gap-2 transition-all duration-200
            ${selectedPoints 
              ? 'bg-primary hover:bg-primary-hover' 
              : 'bg-muted text-muted-foreground cursor-not-allowed'
            }
          `}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};